import get from 'lodash/get';
import has from 'lodash/has';
import set from 'lodash/set';
import { getCountryCodeFromCountryName } from 'cirrus/Dropdown/Country/utils';
import { getRegionCodeFromRegionName } from 'cirrus/Dropdown/Region/utils';
import schema from './validations';

/**
 * Gets the shopAddress data based on the formData
 * @param {Object} formData
 * @return {Object} an object with the address (fields can be undefined)
 */
export const getShopAddress = formData => ({
  countryName: get(formData, 'countryName', ''),
  countryCode: get(formData, 'countryCode', ''),
  regionName: get(formData, 'regionName', ''),
  regionCode: get(formData, 'regionCode', ''),
  address1: formData.address1,
  address2: formData.address2,
  city: formData.city,
  zipcode: formData.zipcode,
});

/**
 * Gets the shopListing by shopId
 * @param {Array} shopListings
 * @param {Number} shopId
 * @return {Object|Undefined} the shopListing or undefined if none could be found
 */
export const getShopListingByShopId = (shopListings, shopId) =>
  shopListings.find(shop => shop.id === shopId);

/**
 * Checks if any of the address fields are in the error object
 * @param {Object} errors
 * @return {Boolean} Whether all of the address fields have no errors
 */
export const hasValidAddress = errors =>
  [
    'address1',
    'address2',
    'city',
    'zipcode',
    'countryName',
    'countryCode',
    'regionName',
    'regionCode',
  ].every(field => !has(errors, field));

/**
 * Formats the form data to the POST format that the API expects
 * @param {Object} formData
 * @return {Object} the format that the API expects
 */
export const formatForAPI = formData => {
  const {
    shopId,
    shopName,
    shopEmail,
    languageCode,
    publish,
    address1,
    address2,
    city,
    zipcode,
    signInEmail,
  } = formData;
  const countryName = get(formData, 'countryName', '');
  const countryCode = get(formData, 'countryCode', '');
  const regionName = get(formData, 'regionName', '');
  const regionCode = get(formData, 'regionCode', '');

  return {
    ecomProvisioning: {
      shopName,
      languageCode,
      publish,
      user: {
        login: signInEmail,
      },
      shop: {
        id: shopId,
        email: shopEmail,
        city,
        zipcode,
        regionCode,
        regionName,
        countryCode,
        countryName,
        address1,
        address2,
      },
    },
  };
};

/**
 * Formats the GET data from the API to the formData to be used for our form
 * @param {Object} data the GET data from the API
 * @return {Object} the object to be used as formData for our form
 */
export const formatInitialValuesFromAPI = data => {
  const ecomProvisioning = get(data, 'ecomProvisioning', {});
  const shopListings = get(data, 'shopListings', []);
  const user = get(data, 'user', {});
  const shopId = get(ecomProvisioning, 'shopId', '');
  const currentShop = getShopListingByShopId(shopListings, shopId);
  const countryName = get(currentShop, 'countryName') || '';
  const countryCode =
    get(currentShop, 'countryCode') || getCountryCodeFromCountryName(countryName) || '';
  const regionName = get(currentShop, 'regionName') || '';
  const regionCode =
    get(currentShop, 'regionCode') ||
    getRegionCodeFromRegionName({ countryCode, regionName }) ||
    '';

  return {
    shopName: get(ecomProvisioning, 'shopName') || '',
    shopId,
    shopEmail: get(currentShop, 'email') || '',
    languageCode: get(ecomProvisioning, 'languageCode') || '',
    countryName,
    countryCode,
    ...{ regionCode, regionName },
    address1: get(currentShop, 'address1') || '',
    address2: get(currentShop, 'address2') || '',
    city: get(currentShop, 'city') || '',
    zipcode: get(currentShop, 'zipcode') || '',
    signInEmail: get(user, 'login') || '',
  };
};

/**
 * Maps the field errors from the API to errors for Formik
 * @param {Object} errors The errors from the API
 * @return {Object} an object with formData field as key and error message as value
 */
export const formatErrorsForFormik = errors => {
  if (!errors) return {};

  const backendErrorMap = {
    'ecomProvisioning.shopName': 'shopName',
    'ecomProvisioning.languageCode': 'languageCode',
    'ecomProvisioning.publish': 'publish',
    'ecomProvisioning.user.login': 'signInEmail',
    'ecomProvisioning.shop.id': 'shopId',
    'ecomProvisioning.shop.email': 'shopEmail',
    'ecomProvisioning.shop.countryName': 'countryName',
    'ecomProvisioning.shop.countryCode': 'countryCode',
    'ecomProvisioning.shop.regionName': 'regionName',
    'ecomProvisioning.shop.regionCode': 'regionCode',
    'ecomProvisioning.shop.address1': 'address1',
    'ecomProvisioning.shop.address2': 'address2',
    'ecomProvisioning.shop.city': 'city',
    'ecomProvisioning.shop.zipcode': 'zipcode',
  };

  return errors.reduce(
    (errorObject, backendError) =>
      set(errorObject, backendErrorMap[get(backendError, 'meta.field')], backendError.detail),
    {}
  );
};

/**
 * Validates the given values and spits out an object with errors if any are found.
 * Key: fieldName
 * Value: error message
 * @param {Object} values the formData values
 */
export const createInitialErrors = values => {
  try {
    schema.validateSync(values, { abortEarly: false });

    return {};
  } catch (e) {
    if (!get(e, 'inner.length')) return {};

    // Create a new errors object
    return e.inner.reduce((errorObject, error) => {
      // eslint-disable-next-line no-param-reassign
      errorObject[error.path] = error.message;

      return errorObject;
    }, {});
  }
};

/**
 * Creates a status object based on the error message to be passed to form fields.
 * @param {String} message the error message
 */
export const createStatusOnError = message => {
  // To get rid of the error messages from yup, we replaced the messages
  // in validations.js to be ' '. We don't want to render the status if this is the case.
  if (!message || message === ' ') return {};

  return {
    type: 'error',
    message,
  };
};
